// ============================================================================
// Types and Interfaces
// ============================================================================

export interface ContentFingerprint {
    firstWord: string;
    lastWord: string;
    length: number;
    wordCount: number;
}

export interface ElementPathStep {
    tag: string;
    index: number;
    total: number;
}

export interface ContentBlock {
    id: string;
    text: string;
    heading: string;
    selector: string;
    elementPath: ElementPathStep[];
    fingerprint: ContentFingerprint;
}

// ============================================================================
// Stable Element Addressing Class
// ============================================================================

export class StableElementAddressing {
    private rootSelector: string;
    public contentBlocks: ContentBlock[];
    private headings: { node: Node, index: number }[];
    private nodeToIndex: WeakMap<Node, number>;
    private headingIndices: Set<number>;

    constructor(rootElement: HTMLElement, contentBlockSelector: string) {
        this.rootSelector = this.getElementSelector(rootElement);
        this.headings = [];
        this.nodeToIndex = new WeakMap();
        this.headingIndices = new Set();
        this.preorderTraversal(rootElement, 0);
        this.contentBlocks = this.extractContentBlocks(rootElement, contentBlockSelector);
    }

    private preorderTraversal(node: Node, index: number) {
        // Store the preorder index for this node
        this.nodeToIndex.set(node, index);

        // If it's a heading, record it
        if (/^H[1-6]$/i.test(node.nodeName)) {
            this.headings.push({ node, index });
            this.headingIndices.add(index);
        }

        let currentIndex = index + 1;
        for (let i = 0; i < node.childNodes.length; i++) {
            const child = node.childNodes[i];
            if (child.nodeType === Node.ELEMENT_NODE) {
                currentIndex = this.preorderTraversal(child, currentIndex);
            }
        }

        return currentIndex;
    }

    private findClosestPrecedingHeading(targetNode: Node): Node | null {
        const targetIndex: number | undefined = this.nodeToIndex.get(targetNode);
        if (targetIndex === undefined) return null;

        // Binary search for the largest heading index < targetIndex
        let left = 0, right = this.headings.length - 1;
        let result: Node | null = null;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            if (this.headings[mid].index < targetIndex) {
                result = this.headings[mid].node;
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        return result;
    }

    private extractContentBlocks(root: HTMLElement, contentBlockSelector: string): ContentBlock[] {
        const blocks: ContentBlock[] = [];
        const blockElements = root.querySelectorAll(contentBlockSelector);
        const processedElements = new Set<Element>();

        blockElements.forEach((element: Element, index: number) => {
            const htmlElement = element as HTMLElement;

            // Skip if this element is already processed or nested within a processed element
            if (processedElements.has(htmlElement) || this.isNestedInProcessedElement(htmlElement, processedElements)) {
                return;
            }

            const text = htmlElement.textContent?.trim();

            if (text) {
                // When the block is itself a heading, use its own text as "closest title" so local search
                // shows the same title for both line and heading (Bug #2153). Otherwise use preceding heading.
                const isHeading = /^H[1-6]$/i.test(htmlElement.tagName);
                const headingText = isHeading
                    ? text
                    : (this.findClosestPrecedingHeading(htmlElement)?.textContent?.trim() || '');

                blocks.push({
                    id: `block-${index}`,
                    text,
                    heading: headingText,
                    selector: this.getStableSelector(htmlElement, root),
                    elementPath: this.getElementPath(htmlElement, root),
                    fingerprint: this.generateFingerprint(text)
                });

                // Mark this element and all its descendants as processed
                this.markElementAndDescendantsAsProcessed(htmlElement, processedElements);
            }
        });

        return blocks;
    }

    private isNestedInProcessedElement(element: Element, processedElements: Set<Element>): boolean {
        let current = element.parentElement;
        while (current) {
            if (processedElements.has(current)) {
                return true;
            }
            current = current.parentElement;
        }
        return false;
    }

    private markElementAndDescendantsAsProcessed(element: Element, processedElements: Set<Element>): void {
        processedElements.add(element);
        // Also mark all descendants as processed to avoid processing nested elements
        const descendants = element.querySelectorAll('*');
        descendants.forEach(descendant => processedElements.add(descendant));
    }

    private getElementSelector(element: HTMLElement): string {
        if (element.id) {
            return `#${element.id}`;
        }

        // Fallback to tag with unique attributes
        const tag = element.tagName.toLowerCase();
        const classes = Array.from(element.classList).join('.');
        return classes ? `${tag}.${classes}` : tag;
    }

    private getStableSelector(element: HTMLElement, root: HTMLElement): string {
        // Try ID first
        if (element.id) {
            return `#${element.id}`;
        }

        // Try unique class combinations
        const classes = Array.from(element.classList);
        if (classes.length > 0) {
            const selector = `.${classes.join('.')}`;
            const matches = root.querySelectorAll(selector);
            if (matches.length === 1) {
                return selector;
            }
        }

        // Fallback to structural path
        return this.getElementPath(element, root)
            .map(step => `${step.tag}:nth-child(${step.index + 1})`)
            .join(' > ');
    }

    private getElementPath(element: HTMLElement, root: HTMLElement): ElementPathStep[] {
        const path: ElementPathStep[] = [];
        let current: HTMLElement | null = element;

        while (current && current !== root && current.parentElement) {
            const parent = current.parentElement as HTMLElement;
            const siblings = Array.from(parent.children) as HTMLElement[];
            const index = siblings.indexOf(current);

            path.unshift({
                tag: current.tagName.toLowerCase(),
                index,
                total: siblings.length
            });

            current = parent;
        }

        return path;
    }

    public resolveElement(block: Partial<ContentBlock>, currentRoot?: HTMLElement | null): HTMLElement | null {
        const root = currentRoot || document.querySelector(this.rootSelector) as HTMLElement;
        if (!root || !block.fingerprint) return null;

        // Try selector first
        if (block.selector && this.isValidSelector(block.selector)) {
            const element = root.querySelector(block.selector) as HTMLElement;
            if (element && this.verifyElement(element, block.fingerprint)) {
                return element;
            }
        }

        // Try element path
        if (block.elementPath) {
            return this.resolveByPath(block.elementPath, root, block.fingerprint);
        }

        return null;
    }

    private isValidSelector(selector: string): boolean {
        return selector.startsWith('#') || selector.startsWith('.') || selector.startsWith('[') || selector.includes(' > ');
    }

    private resolveByPath(path: ElementPathStep[], root: HTMLElement, fingerprint: ContentFingerprint): HTMLElement | null {
        let current: HTMLElement = root;

        for (const step of path) {
            const children = Array.from(current.children) as HTMLElement[];

            // Try exact index first
            if (step.index < children.length) {
                const candidate = children[step.index];
                if (candidate.tagName.toLowerCase() === step.tag) {
                    current = candidate;
                    continue;
                }
            }

            // Fallback: find by tag name
            const tagMatches = children.filter(child =>
                child.tagName.toLowerCase() === step.tag
            );

            if (tagMatches.length === 1) {
                current = tagMatches[0];
                continue;
            } else if (tagMatches.length > 1) {
                // Multiple matches - try to find by content fingerprint
                const match = tagMatches.find(el =>
                    this.verifyElement(el, fingerprint)
                );
                if (match) {
                    current = match;
                    continue;
                }
            }

            // Path resolution failed
            return null;
        }

        // Verify final element
        return this.verifyElement(current, fingerprint) ? current : null;
    }

    private verifyElement(element: HTMLElement, fingerprint: ContentFingerprint): boolean {
        const text = element.textContent?.trim() || '';
        const currentFingerprint = this.generateFingerprint(text);

        return (
            currentFingerprint.firstWord === fingerprint.firstWord &&
            currentFingerprint.lastWord === fingerprint.lastWord &&
            Math.abs(currentFingerprint.length - fingerprint.length) < 20 &&
            Math.abs(currentFingerprint.wordCount - fingerprint.wordCount) < 5
        );
    }

    public generateFingerprint(text: string): ContentFingerprint {
        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        return {
            firstWord: words[0]?.substring(0, 15) || '',
            lastWord: words[words.length - 1]?.substring(-15) || '',
            length: text.length,
            wordCount: words.length
        };
    }

    public refresh(rootElement: HTMLElement, contentBlockSelector: string): void {
        this.rootSelector = this.getElementSelector(rootElement);
        this.headings = [];
        this.nodeToIndex = new WeakMap();
        this.headingIndices = new Set();
        this.preorderTraversal(rootElement, 0);
        this.contentBlocks = this.extractContentBlocks(rootElement, contentBlockSelector);
    }
}